import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { adminApi, ApiError } from '@/lib/api';

/**
 * Auth guard for all /admin/* UI routes. Asks the backend `/admin/auth/whoami`
 * whether the current session cookie is still valid.
 *
 *   - Pending   → small inline "checking…"
 *   - 401       → redirect to /login?next=<current path>
 *   - 200       → render <children>
 */
// Dev bypass: skip the whoami round-trip so Vite dev server works without a
// running backend. Only true under `vite dev`; `vite build` inlines false.
const DEV_BYPASS = import.meta.env.DEV;

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['whoami'],
    queryFn: adminApi.whoami,
    staleTime: 60_000,
    enabled: !DEV_BYPASS,
  });

  if (DEV_BYPASS) return <>{children}</>;
  if (isLoading) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">验证登录状态…</div>;
  if (error instanceof ApiError && error.status === 401) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!data?.ok) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}
